import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createRootFiInviteLink,
  useBusinessEntity,
  useRootFiDefaultSyncConfigV4,
  type IntegrationDataModelSyncSettings,
} from '@commenda/api';
import { integrationTypeDisplayName } from '@commenda/api/sales-tax/types';
import { RootFiIntegration, RootfiCompanyUseCase } from '@prisma/client';

import type { RootFiSyncConfigItemV4 } from '@commenda/types/src/root-fi/types';
import { useNotification } from '../../hooks/useNotification';
import {
  rootFiApplySyncSettingsToRows,
  rootFiCategoryForIntegration,
  rootFiDefaultSyncConfigRows,
  rootFiSyncConfigItemsToInviteLinkPayload,
  rootFiSyncConfigRowsToSyncSettings,
} from '../helpers/RootFiInviteLinkConnectModalSyncForm';
import type { RootFiInviteLinkConnectModalProps } from '../types/rootFiInviteLinkConnectModal';

export const useRootFiInviteLinkConnectModal = ({
  corporationId,
  useCases,
  isOpen,
  onClose,
  onInviteLinkSaved,
  serviceRequestTaskId,
  showSyncConfiguration = false,
}: RootFiInviteLinkConnectModalProps) => {
  const { setNotification } = useNotification();
  const { data: businessEntity } = useBusinessEntity(corporationId);
  const companyNameForInvitePayload = useMemo(
    () =>
      `${businessEntity?.generalCorporationInfo.legalName?.trim() || 'Corporation'}:${corporationId}`,
    [corporationId, businessEntity?.generalCorporationInfo.legalName]
  );

  const { defaultSyncConfig, isLoading: isDefaultsLoading } =
    useRootFiDefaultSyncConfigV4(useCases);

  const [selectedIntegration, setSelectedIntegration] =
    useState<RootFiIntegration | null>(null);
  const [rows, setRows] = useState<RootFiSyncConfigItemV4[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedIntegration(null);
      setRows([]);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const integrationChoicesForPicker = useMemo(() => {
    if (!defaultSyncConfig) return [];
    return (Object.keys(defaultSyncConfig) as RootFiIntegration[])
      .filter(
        (key) =>
          (defaultSyncConfig[key]?.length ?? 0) > 0 &&
          Object.values(RootFiIntegration).includes(key as RootFiIntegration)
      )
      .sort((a, b) =>
        integrationTypeDisplayName(a as RootFiIntegration).localeCompare(
          integrationTypeDisplayName(b as RootFiIntegration)
        )
      );
  }, [defaultSyncConfig]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedIntegration) {
      setRows([]);
      return;
    }
    setRows(
      rootFiDefaultSyncConfigRows(
        defaultSyncConfig?.[selectedIntegration],
        rootFiCategoryForIntegration(selectedIntegration)
      )
    );
  }, [defaultSyncConfig, isOpen, selectedIntegration]);

  const syncSettings = useMemo(
    () => rootFiSyncConfigRowsToSyncSettings(rows),
    [rows]
  );

  const onSyncSettingsChange = useCallback(
    (settings: IntegrationDataModelSyncSettings[]) =>
      setRows((prev) => rootFiApplySyncSettingsToRows(prev, settings)),
    []
  );

  const submitIntegration = async (
    integration: RootFiIntegration,
    syncRows: RootFiSyncConfigItemV4[]
  ) => {
    setIsSubmitting(true);
    try {
      const result = await createRootFiInviteLink({
        corporationId,
        useCases,
        integration,
        companyName: companyNameForInvitePayload.trim(),
        syncConfig: rootFiSyncConfigItemsToInviteLinkPayload(syncRows),
        serviceRequestTaskId,
      });

      if (!result.success || !result.data) {
        setNotification({
          show: true,
          title: 'Could not start connection',
          message:
            typeof result.error === 'string'
              ? result.error
              : 'Something went wrong. Try again.',
          valid: false,
        });
        return;
      }

      onClose();
      onInviteLinkSaved(result.data.inviteLink.inviteLinkId);
    } catch {
      setNotification({
        show: true,
        title: 'Could not start connection',
        message: 'Something went wrong. Try again.',
        valid: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = async () => {
    if (rows.length === 0 || isSubmitting || !selectedIntegration) return;
    await submitIntegration(selectedIntegration, rows);
  };

  const onSelectedIntegrationChange = (
    integration: RootFiIntegration | null
  ) => {
    setSelectedIntegration(integration);
    const isSyncConfigurationEnabled =
      showSyncConfiguration &&
      useCases.includes(RootfiCompanyUseCase.SALES_TAX);
    if (!integration || isSyncConfigurationEnabled || isSubmitting) {
      return;
    }

    const defaultRows = rootFiDefaultSyncConfigRows(
      defaultSyncConfig?.[integration],
      rootFiCategoryForIntegration(integration)
    );
    if (defaultRows.length > 0) {
      submitIntegration(integration, defaultRows).catch(() => undefined);
    }
  };

  const canSubmit =
    Boolean(selectedIntegration) &&
    rows.length > 0 &&
    !isSubmitting &&
    !isDefaultsLoading;

  return {
    isDefaultsLoading,
    isSubmitting,
    selectedIntegration,
    integrationChoicesForPicker,
    syncSettings,
    onSyncSettingsChange,
    onSelectedIntegrationChange,
    canSubmit,
    onSubmit,
  };
};
